import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Button, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import SectionCard from "Components/Common/SectionCard";
import DetailField from "Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import { useSubjectCheckin } from "../api/useSubjectCheckin";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import { loadCodelistsDetailId } from "api/modules/common.option";
import { formatDate, formatTime24Hour } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import { STUDY_PATHS, SUBJECT_CHECKIN_PATHS } from "Routes/paths";

const ViewSubjectCheckin: React.FC = () => {
  document.title = "View Subject Check-In | ZENTIXS VISTA";
  
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);

  const [codelistsLoaded, setCodelistsLoaded] = useState(false);

  // Fetch subject check-in data
  const { SelectQuery: CheckinSelectQuery } = useSubjectCheckin({
    subjectCheckinId: Number(id),
  });

  const rawRecord = CheckinSelectQuery.data?.data?.[0];
  const volunteerId = rawRecord
    ? Number(rawRecord._volunteerid || rawRecord.volunteerid) || 0
    : 0;

  // Fetch check-in checklist questions
  const { SelectAllQuery: ChecklistQuery } = useCheckListMasterService({
    ispagination: false,
    checklisttype: "Subject Check-In",
  });

  const checkinQuestions = useMemo(() => {
    return ChecklistQuery?.data?.data ?? [];
  }, [ChecklistQuery?.data?.data]);

  // Fetch volunteer details
  const { SelectQuery: VolunteerSelectQuery } = useVolunteerService({
    id: volunteerId,
  });
  const volunteerDetails = VolunteerSelectQuery?.data?.data?.[0] || null;

  // Load codelists for checklists
  useEffect(() => {
    const load = async () => {
      await loadCodelistsDetailId(["Checklist_Type_OPTIONS"]);
      setCodelistsLoaded(true);
    };
    load();
  }, []);

  // Sync breadcrumbs
  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Study", link: STUDY_PATHS.LIST },
      { text: "Check-In", link: SUBJECT_CHECKIN_PATHS.LIST },
      { text: "View Check-In Details" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  // Resolve normalized record data
  const data = useMemo(() => {
    if (!rawRecord) return null;
    const normalized: Record<string, any> = {};
    Object.keys(rawRecord).forEach((key) => {
      const normalizedKey = key.startsWith("_") ? key.substring(1) : key;
      normalized[normalizedKey] = rawRecord[key];
    });
    return normalized;
  }, [rawRecord]);

  // Map answered checklist details
  const checkinChecklist = useMemo(() => {
    if (!data?.checklist) return [];
    return data.checklist
      .filter((item: any) =>
        checkinQuestions.some(
          (q: any) => String(q.masterchecklistid) === String(item.questionid),
        ),
      )
      .map((item: any) => ({
        question: item.questiontext,
        answered: item.ischecked === true || String(item.ischecked) === "true",
      }));
  }, [data?.checklist, checkinQuestions]);

  const checkinStatus = useMemo(() => {
    if (!data) return "Pending";
    const low = (data.checkinstatus ?? "").trim().toLowerCase();
    if (low.includes("pending")) return "Pending";
    if (low.includes("checked") && low.includes("in")) return "Checked In";
    if (low.includes("completed")) return "Checked In";
    if (data.checkindate) return "Checked In";
    return "Pending";
  }, [data]);

  const isLoading =
    CheckinSelectQuery.isLoading ||
    ChecklistQuery.isLoading ||
    !codelistsLoaded ||
    (volunteerId > 0 && VolunteerSelectQuery.isFetching);

  if (isLoading) {
    return (
      <div
        className="page-content d-flex align-items-center justify-content-center"
        style={{ minHeight: "80vh" }}
      >
        <FullPageLoader />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Check-In Record Not Found</h4>
            <p className="text-muted">
              The subject check-in record you are looking for does not exist or has been removed.
            </p>
            <Button
              color="primary"
              onClick={() => navigate(SUBJECT_CHECKIN_PATHS.LIST)}
              className="mt-3"
            >
              Back to Check-In List
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Subject Check-In Details</h3>
            <p
              className="text-muted mb-0 d-flex align-items-center flex-wrap"
              style={{ gap: "8px" }}
            >
              <span>Subject Number: Subject {data.subjectno || "--"}</span>
            </p>
          </div>
          <Button
            color="primary"
            outline
            onClick={() => navigate(SUBJECT_CHECKIN_PATHS.LIST)}
            className="fw-600"
          >
            <i className="ri-arrow-left-line me-1" /> Back to Check-In
          </Button>
        </div>

        <div className="d-flex flex-column gap-3">
          {/* Section 1: Check-In Details */}
          <SectionCard
            icon="ri-login-box-line"
            title="Check-In Information"
            subtitle="Details of subject check-in status, schedule, and timing."
          >
            <Row>
              <DetailField
                label="Check-In Status"
                value={
                  <span
                    className={`badge px-3 py-2 rounded-pill fs-12 ${
                      checkinStatus === "Checked In"
                        ? "bg-success-subtle text-success border border-success"
                        : "bg-warning-subtle text-warning border border-warning"
                    }`}
                  >
                    {checkinStatus}
                  </span>
                }
                columns={3}
              />
              <DetailField
                label="Check-In Date"
                value={data.checkindate ? formatDate(data.checkindate) : "--"}
                columns={3}
              />
              <DetailField
                label="Check-In Time"
                value={data.checkintime ? formatTime24Hour(String(data.checkintime).split("+")[0].split("Z")[0].trim()) : "--"}
                columns={3}
              />
              <DetailField
                label="Allocated Locker No"
                value={data.allocatedlockerno || "--"}
                columns={3}
              />
            </Row>
          </SectionCard>

          {/* Section 2: Check-In Checklist */}
          {checkinStatus === "Checked In" && checkinChecklist.length > 0 && (
            <SectionCard
              icon="ri-survey-line"
              title="Belongings / Check-In Checklist"
              subtitle="Safety and compliance checklist items answered during check-in."
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {checkinChecklist.map((item: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 16px",
                      background: "#f8fafc",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div
                      style={{ fontSize: "14.5px", fontWeight: 500, color: "#334155" }}
                    >
                      {item.question}
                    </div>
                    <div>
                      {item.answered ? (
                        <span className="badge bg-success-subtle text-success px-3 py-1.5 rounded-pill fs-12 border border-success">
                          <i className="ri-checkbox-circle-line me-1" /> Yes
                        </span>
                      ) : (
                        <span className="badge bg-light text-muted px-3 py-1.5 rounded-pill fs-12 border border-secondary-subtle">
                          <i className="ri-close-circle-line me-1" /> No
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      </Container>
    </div>
  );
};

export default ViewSubjectCheckin;
